import React, { useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { loadUser } from '../actions/registerUser';
// Same QR library QrScan.js already uses for the profile-link QR code --
// no new dependency for this screen.
var QRCode = require('qrcode.react');

const errorMessage = (err, fallback) =>
  (err.response && err.response.data && err.response.data.msg) || fallback;

// Self-contained: decides its own visibility (isAdmin) and refreshes the
// canonical totpEnabled/isAdmin state via loadUser() after any change,
// rather than the parent (EditProfile.js) having to track any of this.
const TwoFactorSettings = ({ user, loadUser }) => {
  // 'status' | 'enrolling' | 'disabling'
  const [view, setView] = useState('status');
  const [enrollData, setEnrollData] = useState(null); // { otpauthUrl, manualEntryKey }
  const [confirmCode, setConfirmCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user || !user.isAdmin) return null;

  const resetToStatus = () => {
    setView('status');
    setEnrollData(null);
    setConfirmCode('');
    setDisablePassword('');
    setDisableCode('');
  };

  const startEnroll = async () => {
    setBusy(true);
    try {
      const res = await axios.post('/api/auth/totp/enroll');
      setEnrollData(res.data);
      setView('enrolling');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to start two-factor setup.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post('/api/auth/totp/enroll/verify', { code: confirmCode });
      toast.success('Two-factor authentication is now on.');
      resetToStatus();
      loadUser();
    } catch (err) {
      toast.error(errorMessage(err, 'Incorrect code.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post('/api/auth/totp/disable', {
        password: disablePassword,
        code: disableCode,
      });
      toast.success('Two-factor authentication is now off.');
      resetToStatus();
      loadUser();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to turn off two-factor authentication.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-con" style={{ marginTop: '30px' }}>
      <h4>Security</h4>

      {view === 'status' && (
        <div>
          {user.totpEnabled ? (
            <>
              <p>
                <strong>Two-factor authentication is ON.</strong> You'll be
                asked for a 6-digit code from your authenticator app every
                time you log in.
              </p>
              <button
                type="button"
                className="btn"
                onClick={() => setView('disabling')}
              >
                Turn off two-factor authentication
              </button>
            </>
          ) : (
            <>
              <p>Two-factor authentication is off.</p>
              <button
                type="button"
                className="btn"
                onClick={startEnroll}
                disabled={busy}
              >
                {busy ? 'Starting…' : 'Set up two-factor authentication'}
              </button>
            </>
          )}
        </div>
      )}

      {view === 'enrolling' && enrollData && (
        <div>
          <p style={{ color: '#c0392b', fontWeight: 'bold' }}>
            Two-factor authentication is not active yet. Scan the code below
            and enter a code to finish.
          </p>

          <div
            style={{
              background: 'white',
              padding: '12px',
              display: 'inline-block',
            }}
          >
            <QRCode value={enrollData.otpauthUrl} size={180} />
          </div>

          <p>
            Scan this with an authenticator app (Google Authenticator, Authy,
            1Password, etc).
          </p>
          <p>
            Can't scan it? Enter this key manually:{' '}
            <code>{enrollData.manualEntryKey}</code>
          </p>

          <p style={{ color: '#c0392b' }}>
            <span role="img" aria-label="Warning">⚠️</span> If you lose access
            to this authenticator, there is no self-service way to recover --
            make sure your authenticator app backs up its codes before you
            rely on this.
          </p>

          <form onSubmit={confirmEnroll}>
            <div className="form-con">
              <span>Enter the 6-digit code to confirm</span>
              <input
                type="text"
                name="confirmCode"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                required
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn"
              disabled={busy}
              style={{ marginRight: '10px' }}
            >
              {busy ? 'Verifying…' : 'Confirm and turn on'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={resetToStatus}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {view === 'disabling' && (
        <form onSubmit={confirmDisable}>
          <p>
            Confirm your password and a current code to turn off two-factor
            authentication.
          </p>
          <div className="form-con">
            <span>Current Password</span>
            <input
              type="password"
              name="disablePassword"
              autoComplete="off"
              required
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </div>
          <div className="form-con">
            <span>6-digit code</span>
            <input
              type="text"
              name="disableCode"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn"
            disabled={busy}
            style={{ marginRight: '10px' }}
          >
            {busy ? 'Turning off…' : 'Turn off two-factor authentication'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={resetToStatus}
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.registerUser.user,
});

export default connect(mapStateToProps, { loadUser })(TwoFactorSettings);
